import React from 'react'
import Spin from '../../../../common/ui/Spin'
import { useQuery } from '@apollo/client'
import { GET_AGENT_QUERY } from '../../../graphql/queries/agent'
import {
  AgentQuery,
  AgentQueryVariables,
  AgentWorkflowsQuery,
  AgentWorkflowsQueryVariables,
  AnalysesQuery,
  AnalysesQueryVariables,
} from 'app/javascript/components/graphql'
import { useHistory } from 'react-router-dom'
import AgentHeader from './AgentHeader'
import { Tab, TabList, TabPanel, Tabs } from '@mui/joy'
import AgentKnowledge from '../AgentKnowledge'
import { GET_AGENT_WORKFLOWS } from '../../../graphql/queries/workflow'
import { GET_ANALYSES_QUERY } from '../../../graphql/queries/analysis'
import AnalysisCard from '../../../components/AnalysisCard'
import AlertCard from '../../../components/AlertCard'
import EmptyState from '../../../../../components/Walkthrough/ui/EmptyState'

const OneAgent = ({ uuid }: { uuid: string }) => {
  const history = useHistory()

  const { data, loading } = useQuery<AgentQuery, AgentQueryVariables>(GET_AGENT_QUERY, {
    variables: { uuid },
    fetchPolicy: 'network-only',
  })

  const { data: workflowData, loading: workflowLoading } = useQuery<
    AgentWorkflowsQuery,
    AgentWorkflowsQueryVariables
  >(GET_AGENT_WORKFLOWS, {
    variables: {
      agentUuid: uuid,
    },
    fetchPolicy: 'network-only',
  })
  const { data: analysesData, loading: analysesLoading } = useQuery<
    AnalysesQuery,
    AnalysesQueryVariables
  >(GET_ANALYSES_QUERY, {
    variables: {
      agentUuid: uuid,
    },
    fetchPolicy: 'network-only',
  })
  const workflows = workflowData?.agentWorkflows
  const analyses = analysesData?.analyses
  const agent = data?.agent

  if (loading) {
    return <Spin />
  }

  if (!agent) {
    window.toastr.error('Agent not found')
    history.push('/agents')
    return <></>
  }

  if (agent.isCompany) {
    history.push('/agents')
    return <></>
  }

  document.title = agent.label + ' - Agent - FlowMo'

  return (
    <div className="h-full flex flex-col">
      <AgentHeader agent={agent} />

      <Tabs className="mt-10 text-base" orientation="vertical">
        <TabList>
          <Tab>Info</Tab>
          <Tab>Analyses</Tab>
          <Tab>Alerts</Tab>
          <Tab>Knowledge</Tab>
        </TabList>
        <TabPanel value={0}>
          <div className="flex flex-col">
            <div className="flex flex-row items-center relative ">
              <div className="flex-grow flex flex-col space-y-4">
                <p className="flex flex-row space-x-2 font-display text-2xl  tracking-tight sm:text-4xl">
                  <span className="text-slate-700">Hi, I&apos;m an expert</span>
                  <span className="block font-semibold text-flowmo-blue-900">
                    {agent.jobTitle} –
                  </span>
                </p>

                <div className="flex flex-col max-w-2xl">
                  <div className="my-4 text-base tracking-tight text-slate-800">
                    {agent.description}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel value={1}>
          <div className="flex flex-col">
            {analysesLoading ? (
              <Spin />
            ) : (
              <div className="flex flex-col space-y-8">
                {analyses?.map((analysis) => (
                  <AnalysisCard key={analysis.uuid} analysis={analysis} size="small" />
                ))}
              </div>
            )}
            {analyses?.length === 0 && (
              <EmptyState title="No analyses" description="No analyses found for this agent." />
            )}
          </div>
        </TabPanel>
        <TabPanel value={2}>
          <div className="flex flex-col">
            {workflowLoading ? (
              <Spin />
            ) : (
              <div className="flex flex-col space-y-8">
                {workflows?.map((workflow) => (
                  <AlertCard key={workflow.uuid} workflow={workflow} size="small" />
                ))}
              </div>
            )}
            {workflows?.length === 0 && (
              <EmptyState title="No alerts" description="No alerts found for this agent." />
            )}
          </div>
        </TabPanel>
        <TabPanel value={3}>
          <AgentKnowledge agent={agent} />
        </TabPanel>
      </Tabs>
    </div>
  )
}

export default OneAgent
